import { createContext, useContext, useMemo, type ReactNode } from "react";
import { DEFAULT_LOCALE, localizePath, type Locale } from "./config";
import { CATEGORY_LABELS, translate, type MessageKey } from "./dictionaries";

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

export const LocaleProvider = ({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) => <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;

export const useLocale = (): Locale => useContext(LocaleContext);

/** Translate an interface string in the active locale. */
export const useT = () => {
  const locale = useLocale();
  return useMemo(() => (key: MessageKey) => translate(locale, key), [locale]);
};

/** Build a full href (with locale prefix) for a plain app path. */
export const useLocalizedHref = () => {
  const locale = useLocale();
  return useMemo(() => (path: string) => localizePath(path, locale), [locale]);
};

/** Translated label for a subject/category chip, falling back to the original. */
export const useCategoryLabel = () => {
  const locale = useLocale();
  return useMemo(
    () => (label: string) => {
      if (locale === "en") return label;
      return CATEGORY_LABELS[label]?.[locale] ?? label;
    },
    [locale],
  );
};
